// This file is auto-generated by @hey-api/openapi-ts

import {
  type Options,
  getRoot,
  getSystemOverview,
  getCameraInterfaces,
  applyCameraInterfaces,
  getSystemLogs,
  getCitysenseCarLogs,
  downloadDebugLog,
  downloadErrorLog,
  startCitysenseServices,
  stopCitysenseServices,
  startCalibration,
  getCalibrationStatus,
  getCalibrationPreview,
  saveCalibration,
  abortCalibration,
  getCalibrationLog,
  getCalibrationFiles,
} from '../sdk.gen'
import { queryOptions, type UseMutationOptions, type DefaultError } from '@tanstack/react-query'
import type {
  GetRootData,
  GetSystemOverviewData,
  GetCameraInterfacesData,
  ApplyCameraInterfacesData,
  ApplyCameraInterfacesResponse,
  GetSystemLogsData,
  GetCitysenseCarLogsData,
  DownloadDebugLogData,
  DownloadErrorLogData,
  StartCitysenseServicesData,
  StartCitysenseServicesResponse,
  StopCitysenseServicesData,
  StopCitysenseServicesResponse,
  StartCalibrationData,
  StartCalibrationResponse,
  GetCalibrationStatusData,
  GetCalibrationPreviewData,
  SaveCalibrationData,
  SaveCalibrationResponse,
  AbortCalibrationData,
  AbortCalibrationResponse,
  GetCalibrationLogData,
  GetCalibrationFilesData,
} from '../types.gen'
import { client as _heyApiClient } from './initializer'

export type QueryKey<TOptions extends Options> = [
  Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
    _id: string
    _infinite?: boolean
    tags?: ReadonlyArray<string>
  },
]

const createQueryKey = <TOptions extends Options>(
  id: string,
  options?: TOptions,
  infinite?: boolean,
  tags?: ReadonlyArray<string>
): [QueryKey<TOptions>[0]] => {
  const params: QueryKey<TOptions>[0] = {
    _id: id,
    baseUrl: options?.baseUrl || (options?.client ?? _heyApiClient).getConfig().baseUrl,
  } as QueryKey<TOptions>[0]
  if (infinite) {
    params._infinite = infinite
  }
  if (tags) {
    params.tags = tags
  }
  if (options?.body) {
    params.body = options.body
  }
  if (options?.headers) {
    params.headers = options.headers
  }
  if (options?.path) {
    params.path = options.path
  }
  if (options?.query) {
    params.query = options.query
  }
  return [params]
}

export const getRootQueryKey = (options?: Options<GetRootData>) => createQueryKey('getRoot', options)

/**
 * Service description
 * Returns the service name and the list of available endpoints.
 */
export const getRootOptions = (options?: Options<GetRootData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getRoot({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getRootQueryKey(options),
  })
}

export const getSystemOverviewQueryKey = (options?: Options<GetSystemOverviewData>) =>
  createQueryKey('getSystemOverview', options)

/**
 * Aggregated system health overview
 * Evaluates all system predicates (services, network, GPS, TPU, storage, cameras, temperatures) and aggregates their results. Predicate results are cached per predicate; predicates whose dependencies fail are reported as skipped.
 */
export const getSystemOverviewOptions = (options?: Options<GetSystemOverviewData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getSystemOverview({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getSystemOverviewQueryKey(options),
  })
}

export const getCameraInterfacesQueryKey = (options?: Options<GetCameraInterfacesData>) =>
  createQueryKey('getCameraInterfaces', options)

/**
 * Propose camera interface names
 * Lists cameras via Pylon inside the citysense-car container, matches them to physical NICs through ARP neighbours and proposes the expected interface name for each camera. Never modifies the system.
 */
export const getCameraInterfacesOptions = (options?: Options<GetCameraInterfacesData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getCameraInterfaces({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getCameraInterfacesQueryKey(options),
  })
}

export const applyCameraInterfacesQueryKey = (options?: Options<ApplyCameraInterfacesData>) =>
  createQueryKey('applyCameraInterfaces', options)

/**
 * Apply proposed camera interface names
 * Recomputes the proposal and renames the matched NICs using apply-camera-interface-names.sh. On success, schedules citysense-update-connections to restart after a short delay. Refuses to apply when conflicts are present or when there is nothing to apply.
 */
export const applyCameraInterfacesOptions = (options?: Options<ApplyCameraInterfacesData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await applyCameraInterfaces({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: applyCameraInterfacesQueryKey(options),
  })
}

/**
 * Apply proposed camera interface names
 * Recomputes the proposal and renames the matched NICs using apply-camera-interface-names.sh. On success, schedules citysense-update-connections to restart after a short delay. Refuses to apply when conflicts are present or when there is nothing to apply.
 */
export const applyCameraInterfacesMutation = (
  options?: Partial<Options<ApplyCameraInterfacesData>>
): UseMutationOptions<ApplyCameraInterfacesResponse, DefaultError, Options<ApplyCameraInterfacesData>> => {
  const mutationOptions: UseMutationOptions<
    ApplyCameraInterfacesResponse,
    DefaultError,
    Options<ApplyCameraInterfacesData>
  > = {
    mutationFn: async localOptions => {
      const { data } = await applyCameraInterfaces({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const getSystemLogsQueryKey = (options?: Options<GetSystemLogsData>) => createQueryKey('getSystemLogs', options)

/**
 * Tail of the full systemd journal
 * Last 250 lines of journalctl --no-pager (all units including kernel).
 */
export const getSystemLogsOptions = (options?: Options<GetSystemLogsData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getSystemLogs({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getSystemLogsQueryKey(options),
  })
}

export const getCitysenseCarLogsQueryKey = (options?: Options<GetCitysenseCarLogsData>) =>
  createQueryKey('getCitysenseCarLogs', options)

/**
 * Tail of citysense-car journal
 * Last 250 lines of journalctl -u citysense-car --no-pager.
 */
export const getCitysenseCarLogsOptions = (options?: Options<GetCitysenseCarLogsData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getCitysenseCarLogs({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getCitysenseCarLogsQueryKey(options),
  })
}

export const downloadDebugLogQueryKey = (options?: Options<DownloadDebugLogData>) =>
  createQueryKey('downloadDebugLog', options)

/**
 * Download the full debug log
 * Streams debug.jsonl as an attachment. Returns an empty body when no log directory or file is found.
 */
export const downloadDebugLogOptions = (options?: Options<DownloadDebugLogData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await downloadDebugLog({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: downloadDebugLogQueryKey(options),
  })
}

export const downloadErrorLogQueryKey = (options?: Options<DownloadErrorLogData>) =>
  createQueryKey('downloadErrorLog', options)

/**
 * Download the full error log
 * Streams error.jsonl as an attachment. Returns an empty body when no log directory or file is found.
 */
export const downloadErrorLogOptions = (options?: Options<DownloadErrorLogData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await downloadErrorLog({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: downloadErrorLogQueryKey(options),
  })
}

export const startCitysenseServicesQueryKey = (options?: Options<StartCitysenseServicesData>) =>
  createQueryKey('startCitysenseServices', options)

/**
 * Start CitySense services
 * Starts the citysense-orchestrator systemd unit.
 */
export const startCitysenseServicesOptions = (options?: Options<StartCitysenseServicesData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await startCitysenseServices({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: startCitysenseServicesQueryKey(options),
  })
}

/**
 * Start CitySense services
 * Starts the citysense-orchestrator systemd unit.
 */
export const startCitysenseServicesMutation = (
  options?: Partial<Options<StartCitysenseServicesData>>
): UseMutationOptions<StartCitysenseServicesResponse, DefaultError, Options<StartCitysenseServicesData>> => {
  const mutationOptions: UseMutationOptions<
    StartCitysenseServicesResponse,
    DefaultError,
    Options<StartCitysenseServicesData>
  > = {
    mutationFn: async localOptions => {
      const { data } = await startCitysenseServices({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const stopCitysenseServicesQueryKey = (options?: Options<StopCitysenseServicesData>) =>
  createQueryKey('stopCitysenseServices', options)

/**
 * Stop CitySense services
 * Stops the citysense-orchestrator unit and, on success, also citysense-car and citysense-sync.
 */
export const stopCitysenseServicesOptions = (options?: Options<StopCitysenseServicesData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await stopCitysenseServices({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: stopCitysenseServicesQueryKey(options),
  })
}

/**
 * Stop CitySense services
 * Stops the citysense-orchestrator unit and, on success, also citysense-car and citysense-sync.
 */
export const stopCitysenseServicesMutation = (
  options?: Partial<Options<StopCitysenseServicesData>>
): UseMutationOptions<StopCitysenseServicesResponse, DefaultError, Options<StopCitysenseServicesData>> => {
  const mutationOptions: UseMutationOptions<
    StopCitysenseServicesResponse,
    DefaultError,
    Options<StopCitysenseServicesData>
  > = {
    mutationFn: async localOptions => {
      const { data } = await stopCitysenseServices({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const startCalibrationQueryKey = (options: Options<StartCalibrationData>) =>
  createQueryKey('startCalibration', options)

/**
 * Start a headless calibration session
 * Stops CitySense services, starts the headless calibrator for a single side (FL or FR) and returns immediately. Poll /calibration/status and /calibration/preview.jpg while the session runs. One successful save completes the session. Returns 409 when a session is already active.
 */
export const startCalibrationOptions = (options: Options<StartCalibrationData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await startCalibration({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: startCalibrationQueryKey(options),
  })
}

/**
 * Start a headless calibration session
 * Stops CitySense services, starts the headless calibrator for a single side (FL or FR) and returns immediately. Poll /calibration/status and /calibration/preview.jpg while the session runs. One successful save completes the session. Returns 409 when a session is already active.
 */
export const startCalibrationMutation = (
  options?: Partial<Options<StartCalibrationData>>
): UseMutationOptions<StartCalibrationResponse, DefaultError, Options<StartCalibrationData>> => {
  const mutationOptions: UseMutationOptions<StartCalibrationResponse, DefaultError, Options<StartCalibrationData>> = {
    mutationFn: async localOptions => {
      const { data } = await startCalibration({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const getCalibrationStatusQueryKey = (options?: Options<GetCalibrationStatusData>) =>
  createQueryKey('getCalibrationStatus', options)

/**
 * Calibration session status
 * Aggregates process state with the calibrator's state.json (position, success, seq).
 */
export const getCalibrationStatusOptions = (options?: Options<GetCalibrationStatusData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getCalibrationStatus({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getCalibrationStatusQueryKey(options),
  })
}

export const getCalibrationPreviewQueryKey = (options?: Options<GetCalibrationPreviewData>) =>
  createQueryKey('getCalibrationPreview', options)

/**
 * Latest calibration preview frame
 * JPEG overlay produced by the headless calibrator. Poll about once per second. Returns 404 until the first frame is available.
 */
export const getCalibrationPreviewOptions = (options?: Options<GetCalibrationPreviewData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getCalibrationPreview({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getCalibrationPreviewQueryKey(options),
  })
}

export const saveCalibrationQueryKey = (options?: Options<SaveCalibrationData>) =>
  createQueryKey('saveCalibration', options)

/**
 * Save the current side calibration
 * Writes a save command for the calibrator. Only accepted while the session is running and status.success is true. One successful save completes the session for the started side.
 */
export const saveCalibrationOptions = (options?: Options<SaveCalibrationData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await saveCalibration({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: saveCalibrationQueryKey(options),
  })
}

/**
 * Save the current side calibration
 * Writes a save command for the calibrator. Only accepted while the session is running and status.success is true. One successful save completes the session for the started side.
 */
export const saveCalibrationMutation = (
  options?: Partial<Options<SaveCalibrationData>>
): UseMutationOptions<SaveCalibrationResponse, DefaultError, Options<SaveCalibrationData>> => {
  const mutationOptions: UseMutationOptions<SaveCalibrationResponse, DefaultError, Options<SaveCalibrationData>> = {
    mutationFn: async localOptions => {
      const { data } = await saveCalibration({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const abortCalibrationQueryKey = (options?: Options<AbortCalibrationData>) =>
  createQueryKey('abortCalibration', options)

/**
 * Abort the calibration session
 * Sends quit to the calibrator and, if it does not exit within a short grace period, kills the calibration container. The run script restarts the orchestrator on exit.
 */
export const abortCalibrationOptions = (options?: Options<AbortCalibrationData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await abortCalibration({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: abortCalibrationQueryKey(options),
  })
}

/**
 * Abort the calibration session
 * Sends quit to the calibrator and, if it does not exit within a short grace period, kills the calibration container. The run script restarts the orchestrator on exit.
 */
export const abortCalibrationMutation = (
  options?: Partial<Options<AbortCalibrationData>>
): UseMutationOptions<AbortCalibrationResponse, DefaultError, Options<AbortCalibrationData>> => {
  const mutationOptions: UseMutationOptions<AbortCalibrationResponse, DefaultError, Options<AbortCalibrationData>> = {
    mutationFn: async localOptions => {
      const { data } = await abortCalibration({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const getCalibrationLogQueryKey = (options?: Options<GetCalibrationLogData>) =>
  createQueryKey('getCalibrationLog', options)

/**
 * Calibration run log
 * Plain-text stdout/stderr of run-calibration.sh --headless for the current or last session.
 */
export const getCalibrationLogOptions = (options?: Options<GetCalibrationLogData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getCalibrationLog({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getCalibrationLogQueryKey(options),
  })
}

export const getCalibrationFilesQueryKey = (options?: Options<GetCalibrationFilesData>) =>
  createQueryKey('getCalibrationFiles', options)

/**
 * Installed calibration files
 * Existence and mtime of /etc/citysense/calibration-FL.json and calibration-FR.json.
 */
export const getCalibrationFilesOptions = (options?: Options<GetCalibrationFilesData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getCalibrationFiles({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getCalibrationFilesQueryKey(options),
  })
}
